/**
 * @typedef PrintProductConfiguration Defines a PrintProductConfigurationItem
 * @property {Object} print_product the print product
 * @property {Object} print_product_size the print product size
 * @property {Object} print_product_size_services the print services, including total & records
 */

/**
 * Given a printProductConfiguration, retrieve the  print_product_size_services available
 *
 * @param {PrintProductConfiguration} printProductConfiguration the print product configuration
 * @returns {Array}
 */
export const getPrintProductSizeServices = printProductConfiguration => {
  if (!printProductConfiguration?.print_product_size_services?.records) {
    return [];
  }

  return printProductConfiguration.print_product_size_services.records;
};

/**
 * Given a printProductConfiguration, retrieve the related print service labels
 *
 * @param {PrintProductConfiguration} printProductConfiguration the print product configuration
 * @returns {Array}
 */
export const getPrintServiceLabels = printProductConfiguration => {
  if (!printProductConfiguration?.print_product_size_services?.records) {
    return [];
  }

  return printProductConfiguration.print_product_size_services.records.map(
    ({ print_product_size_service }) =>
      print_product_size_service.print_service.name,
  );
};

/**
 * Given a printProductConfiguration, retrieve the related print size service ids
 *
 * @param {PrintProductConfiguration} printProductConfiguration the print product configuration
 * @returns {Array}
 */
export const getPrintSizeServiceIds = printProductConfiguration => {
  if (!printProductConfiguration?.print_product_size_services?.records) {
    return [];
  }

  return printProductConfiguration.print_product_size_services.records.map(
    ({ print_product_size_service }) => print_product_size_service.id,
  );
};

/**
 * Given a printProductConfiguration, determine if the config has the given printService enabled
 *
 * @param {PrintProductConfiguration} printProductConfiguration the print product configuration
 * @param {String} printServiceId the print service id
 * @returns {boolean}
 */
export const hasPrintSizeService = (
  printProductConfiguration,
  printServiceId,
) => {
  if (!printProductConfiguration?.print_product_size_services?.records) {
    return false;
  }

  return !!printProductConfiguration.print_product_size_services.records.find(
    configPrintProductSizeService =>
      configPrintProductSizeService.print_product_size_service?.print_service
        ?.id === printServiceId,
  );
};

/**
 * Given a printProductConfiguration, retrieve the price of the print size.
 *
 * @param {PrintProductConfiguration} printProductConfiguration the print product configuration
 * @returns {number}
 */
export const getPrintSizePrice = printProductConfiguration => {
  if (!printProductConfiguration) {
    return 0;
  }

  const productSize = printProductConfiguration?.print_product_size;

  if (!productSize) {
    return 0;
  }

  if (productSize.min_price) {
    return productSize.min_price.value;
  }

  return productSize.price?.value ?? 0;
};

/**
 * Given a printProductConfiguration and serviceId, retrieve the price of the service.
 *
 * @param {PrintProductConfiguration} printProductConfiguration the print product configuration
 * @param {String} printServiceId the print service id
 * @returns {number}
 */
export const getPrintSizeServicePrice = (
  printProductConfiguration,
  printServiceId,
) => {
  if (!printProductConfiguration) {
    return 0;
  }

  const productSizeService = (
    printProductConfiguration.print_product_size_services?.records ?? []
  ).find(
    configPrintProductSizeService =>
      configPrintProductSizeService.print_product_size_service?.print_service
        ?.id === printServiceId,
  );

  if (!productSizeService) {
    return 0;
  }

  return productSizeService.print_product_size_service?.price?.value ?? 0;
};

/**
 *
 * @param {PrintProductConfiguration} printProductConfiguration the print product configuration
 * @param {Array} printServiceIds the print service ids
 * @returns {number}
 */
export const getPrintSizeServicesTotalPrice = (
  printProductConfiguration,
  printServiceIds,
) => {
  if (!printProductConfiguration || (printServiceIds?.length ?? 0) === 0) {
    return 0;
  }

  return printServiceIds.reduce((sum, printServiceId) => {
    return (
      sum + getPrintSizeServicePrice(printProductConfiguration, printServiceId)
    );
  }, 0);
};

/**
 * Given a printProductConfiguration, retrieve the  roes graphic nodes available
 *
 * @param {PrintProductConfiguration} printProductConfiguration the print product configuration
 * @returns {Array}
 */
export const getPrintProductSizeGraphicNodes = printProductConfiguration => {
  if (
    !printProductConfiguration?.print_product_size?.roes_info?.graphic_nodes
      ?.records
  ) {
    return [];
  }

  return printProductConfiguration.print_product_size.roes_info.graphic_nodes
    .records;
};
